import React from 'react';
import { getRepresentatives } from '../../../services/projectRepresentatives';
import { kuerze, monatText, tagText, verbinde } from '../handbuchLogic';
import { ROLLE_LABEL, orgModellKonfig } from '../orgModelle';
import type { OrgModellKonfig } from '../orgModelle';
import type { Contact, ReportMetadata, VorankuendigungData } from '../../../types';
import type {
    BeteiligterRolle, GewerkAusPlan, Handbuch, HandbuchGewerk, HandbuchNachweis,
} from '../types';
import { A3Seite } from './A3Seite';
import { Abschnitt, AbschnittLeer, AbschnittTabelle, AbschnittZeile, AbschnittZelle } from './Abschnitt';
import { HANDBUCH_FARBEN, HANDBUCH_SCHRIFT } from './stil';

/**
 * Kapitel 2 — Organigramm und Vorankündigung.
 *
 * Portiert aus `pages/editor/OrganigrammPage.tsx` der Quell-App (550 Z.):
 * die SVG-Grundformen (:8-72) und das Zeilenlayout je Vergabemodell (:88 ff.).
 *
 * ═══ HANDGESCHRIEBENES SVG, KEIN REACTFLOW ═════════════════════════════════
 * Die Quell-App zeichnet das Organigramm selbst; `@xyflow/react` kommt dort
 * gar nicht vor. Das bleibt so: ein Diagramm, das gedruckt werden muss, will
 * feste Koordinaten und keine Physik-Simulation — und eine neue Abhaengigkeit
 * nur fuer neun Kaesten waere in keinem Verhaeltnis.
 *
 * ═══ DIESES KAPITEL SCHREIBT NICHTS ════════════════════════════════════════
 * Es liest Projektstammdaten, Verteiler, Vorankuendigung und die Gewerke der
 * Planfassung. Kein Feld dieser Seite geht zurueck in `metadata`,
 * `distributionList` oder `report.vorankuendigung`. Praezedenzfall im Repo ist
 * die Vorankuendigung selbst („liest die Projektdaten nur, schreibt nie
 * hinein").
 *
 * ═══ DREI ABWEICHUNGEN VON DER VORLAGE, JEDE MIT GRUND ═════════════════════
 * 1. Der SiGeKo haengt als GESTRICHELTE STABSSTELLE am Bauherrn und steht
 *    nicht in der Linie der uebrigen Beteiligten. Er ist beratend und nicht
 *    weisungsbefugt; die Vorlage stellte ihn neben Planer und Bauleitung, was
 *    ein Weisungsverhaeltnis behauptet, das es nicht gibt.
 * 2. Die uebrigen Beteiligten haengen als SEITENZWEIGE an der Senkrechten
 *    unter dem Bauherrn. Die Vorlage reihte sie waagerecht auf und liess die
 *    Gewerke-Senkrechte anschliessend mitten durch diese Reihe laufen.
 * 3. Die Gewerkekaesten werden auf den verfuegbaren Platz GERECHNET. Die
 *    Vorlage nahm feste 90 px Breite bei einem Abstand von 71 px — ab sieben
 *    Gewerken zeichnete sie die Kaesten uebereinander.
 */

type SprungZiel = 'stammdaten' | 'verteiler' | 'vorankuendigung' | 'sigeplan' | 'handbuch';

interface Props {
    /** Projektstammdaten: Bauherr, Koordinator, Bauleitung vor Ort, Auftragnehmer, Verteiler. */
    metadata: ReportMetadata;
    /** Handbuchkopf — liefert das Vergabemodell und die Kopfzeilenangaben. */
    handbuch: Handbuch | null;
    /** `report.vorankuendigung`. Fehlt sie, sagt das Kapitel das und erfindet nichts. */
    vorankuendigung?: VorankuendigungData;
    /** Die angelegten Gewerkeseiten. */
    gewerke: HandbuchGewerk[];
    /** Die Gewerke der Planfassung — Ersatzquelle, solange keine Seite angelegt
     *  ist, und Zeitraumquelle, wenn die Seite keinen eigenen traegt. */
    planGewerke?: GewerkAusPlan[];
    /** Fuer die Nachweiszaehlung im Gewerkekasten. */
    nachweise?: HandbuchNachweis[];
    /** Kopfzeile der Seite, ueblicherweise ein `<SeitenKopf …/>`. */
    kopf?: React.ReactNode;
    /** Fusszeile der Seite, ueblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    onSprung?: (ziel: SprungZiel) => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// MASSE
// ═══════════════════════════════════════════════════════════════════════════
//
// Portiert aus `OrganigrammPage.tsx:8-14`. Die Zeichenflaeche ist fest; sie
// wird ueber `viewBox` in den vorhandenen Platz skaliert. Feste Koordinaten
// sind bei einem Druckerzeugnis der Punkt, nicht der Mangel.

const SVG_W = 680;
const SVG_H = 380;
const BOX_W = 120;
const BOX_H = 28;
const GEWERK_W = 90;
const GEWERK_H = 54;
const CX = SVG_W / 2;
/** Waagerechter Abstand der Seitenzweige von der Senkrechten. */
const ZWEIG_DX = 210;
/** Mehr Gewerkekaesten passen nicht lesbar nebeneinander; der Rest wird
 *  gezaehlt und benannt, nicht verschwiegen. */
const MAX_GEWERKE_SICHTBAR = 8;

// ═══════════════════════════════════════════════════════════════════════════
// REINE FUNKTIONEN
//
// `tagText`, `monatText`, `kuerze` und `verbinde` sind nach `handbuchLogic.ts`
// gezogen — sie standen mehrfach in den Kapiteln und sind dort geprueft. Was
// hier steht, ist Kastengeometrie und Rollenzuordnung dieses einen Blatts.
// Nicht exportiert: `react-refresh/only-export-components` meldet sonst bei
// `--max-warnings 0`.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Trifft die Rollenbezeichnung eines Kontakts eine Organigramm-Rolle?
 *
 * BEWUSST KONSERVATIV: eine feste Stichwortliste, kein Aehnlichkeitsmass. Die
 * stehende Lehre im Repo lautet, dass automatisches Zusammenfuehren nach
 * Namensaehnlichkeit stillschweigend Firmen verschwinden laesst. Passt nichts,
 * ist die Antwort `null` — „ich weiss es nicht" — und der Kasten bleibt als
 * unbesetzt markiert stehen.
 *
 * „koordinator" und „sige" schliessen die Planerrolle AUS: ein „Koordinator
 * Planung" ist der SiGeKo und nicht der Architekt.
 */
const STICHWORTE: { rolle: BeteiligterRolle; treffer: string[]; sperre?: string[] }[] = [
    { rolle: 'planer', treffer: ['architekt', 'planer', 'planung', 'entwurf'], sperre: ['koordinator', 'sige'] },
    { rolle: 'bauleitung', treffer: ['bauleit', 'bauführ', 'baufuehr', 'polier', 'objektüberwach', 'objektuberwach'] },
    { rolle: 'gu', treffer: ['generalunternehm'] },
    { rolle: 'gue', treffer: ['generalübernehm', 'generaluebernehm'] },
    { rolle: 'tu', treffer: ['totalunternehm'] },
    { rolle: 'tragwerksplaner', treffer: ['tragwerk', 'statik'] },
    { rolle: 'bodengutachter', treffer: ['bodengutacht', 'geotechn', 'baugrund'] },
];

const rolleAusText = (text: string | undefined): BeteiligterRolle | null => {
    const t = (text || '').toLowerCase();
    if (!t.trim()) return null;
    for (const eintrag of STICHWORTE) {
        if (eintrag.sperre?.some(s => t.includes(s))) continue;
        if (eintrag.treffer.some(s => t.includes(s))) return eintrag.rolle;
    }
    return null;
};

/** Ein Kasten des Organigramms, bereits mit Text gefuellt. */
interface Kasten {
    rolle: BeteiligterRolle;
    /** Was oben im Kasten steht. */
    label: string;
    /** Was klein darunter steht — Firma oder Person. */
    sub: string;
    /** Volltext fuer die Beteiligtentabelle. */
    firma: string;
    person: string;
    telefon: string;
    /** Niemand hinterlegt. Der Kasten wird trotzdem gezeichnet: das
     *  Vergabemodell verlangt ihn, und eine unbesetzte Rolle ist eine
     *  Feststellung, kein Grund zum Weglassen. */
    fehlt: boolean;
}

const kontaktKasten = (rolle: BeteiligterRolle, k: Contact | undefined): Kasten => ({
    rolle,
    label: ROLLE_LABEL[rolle],
    sub: k ? (k.company || k.name || '') : '',
    firma: k?.company || '',
    person: k?.name || '',
    telefon: k?.phone || '',
    fehlt: !k,
});

/**
 * Wer besetzt welche Rolle?
 *
 * Reihenfolge der Quellen je Rolle — die Stammdaten schlagen den Verteiler,
 * weil sie gepflegt und nicht bloss eingeladen sind:
 *   * Bauleitung  → `metadata.siteContact*`, ersatzweise ein Verteilerkontakt
 *   * GU/GÜ/TU    → `metadata.contractorCompany/Name`, ersatzweise Verteiler
 *   * Planer      → nur Verteiler (in OS-IA gibt es dafuer kein Stammfeld)
 * Der SiGeKo kommt NICHT aus dem Verteiler: das sind wir selbst, und er steht
 * in `representatives[0]`.
 */
const besetzung = (konfig: OrgModellKonfig, metadata: ReportMetadata): Kasten[] => {
    const verteiler = metadata.distributionList || [];
    const ausVerteiler = (rolle: BeteiligterRolle): Contact | undefined =>
        verteiler.find(k => rolleAusText(k.role) === rolle);

    return konfig.beteiligteRollen
        .filter(r => r !== 'sigeko')
        .map((rolle): Kasten => {
            if (rolle === 'bauleitung' && (metadata.siteContactCompany || metadata.siteContactPerson)) {
                return {
                    rolle,
                    label: ROLLE_LABEL[rolle],
                    sub: metadata.siteContactCompany || metadata.siteContactPerson || '',
                    firma: metadata.siteContactCompany || '',
                    person: metadata.siteContactPerson || '',
                    telefon: metadata.siteContactPhone || '',
                    fehlt: false,
                };
            }
            if ((rolle === 'gu' || rolle === 'gue' || rolle === 'tu')
                && (metadata.contractorCompany || metadata.contractorName)) {
                return {
                    rolle,
                    label: ROLLE_LABEL[rolle],
                    sub: metadata.contractorCompany || metadata.contractorName || '',
                    firma: metadata.contractorCompany || '',
                    person: metadata.contractorName || '',
                    telefon: '',
                    fehlt: false,
                };
            }
            return kontaktKasten(rolle, ausVerteiler(rolle));
        });
};

/** Der SiGeKo als Stabsstelle. Quelle ist die Vertreterliste; die
 *  `coordinator*`-Felder sind nur ihr Spiegel und decken den Altbestand. */
const sigekoKasten = (metadata: ReportMetadata): Kasten => {
    const erster = getRepresentatives(metadata)[0];
    const name = erster?.name || metadata.coordinator || '';
    return {
        rolle: 'sigeko',
        label: 'SiGeKo',
        sub: name,
        firma: '',
        person: name,
        telefon: erster?.phone || metadata.coordinatorPhone || '',
        fehlt: !name.trim(),
    };
};

/** Ein Gewerkekasten mit allem, was hineingeschrieben wird. */
interface GewerkKasten {
    schluessel: string;
    label: string;
    firma: string;
    von: string;
    bis: string;
    personen: number | null;
    dokOk: number;
    dokPruef: number;
    dokFehlt: number;
    dokGesamt: number;
    /** Nur aus dem Plan abgeleitet, es gibt noch keine Gewerkeseite dazu. */
    nurPlan: boolean;
}

/**
 * Fuehrt Gewerkeseiten, Planfassung und Nachweise zu Kaesten zusammen.
 *
 * Der Zeitraum kommt aus der Seite, solange sie einen traegt — sonst aus dem
 * Plan. Genau so ist die Spalte gemeint (`NULL` = aus den Arbeitsschritten
 * rechnen); ein festgeschriebener Zeitraum wuerde spaetere Planaenderungen im
 * Handbuch unsichtbar machen.
 *
 * Gibt es noch keine Seite, zeichnet das Organigramm die Gewerke des Plans —
 * mit dem Vermerk „aus dem Plan". Ein leeres Organigramm waere die falsche
 * Auskunft: die Gewerke gibt es, es fehlt nur ihre Handbuchseite.
 */
const gewerkKaesten = (
    gewerke: HandbuchGewerk[],
    planGewerke: GewerkAusPlan[],
    nachweise: HandbuchNachweis[],
): GewerkKasten[] => {
    if (!gewerke.length) {
        return planGewerke.map(p => ({
            schluessel: p.gewerk_norm,
            label: p.gewerk_label,
            firma: '',
            von: p.zeitraum_von || '',
            bis: p.zeitraum_bis || '',
            personen: null,
            dokOk: 0, dokPruef: 0, dokFehlt: 0, dokGesamt: 0,
            nurPlan: true,
        }));
    }
    const ausPlan = new Map(planGewerke.map(p => [p.gewerk_norm, p]));
    return gewerke.map(g => {
        const plan = ausPlan.get(g.gewerk_norm);
        const eigene = nachweise.filter(n => n.handbuch_gewerk_id === g.id);
        return {
            schluessel: g.gewerk_norm,
            label: g.gewerk_label,
            firma: g.firma || '',
            von: g.zeitraum_von || plan?.zeitraum_von || '',
            bis: g.zeitraum_bis || plan?.zeitraum_bis || '',
            personen: typeof g.anzahl_beschaeftigte === 'number' ? g.anzahl_beschaeftigte : null,
            dokOk: eigene.filter(n => n.status === 'vorhanden').length,
            dokPruef: eigene.filter(n => n.status === 'pruefung').length,
            dokFehlt: eigene.filter(n => n.status === 'fehlt').length,
            dokGesamt: eigene.length,
            nurPlan: false,
        };
    });
};

/**
 * Die Senkrechten und Waagerechten des Diagramms, je Vergabemodell gerechnet.
 *
 * Ersetzt die Reihenkonstanten der Vorlage (`ROW1`…`ROW5`), die fuer jedes
 * Modell dieselben Werte annahmen und deshalb beim Totalunternehmer eine
 * leere Reihe stehen liessen.
 */
interface Zeilen {
    projekt: number;
    bauherr: number;
    /** y-Werte der Seitenzweige, in Reihenfolge der Rollen. */
    zweige: number[];
    /** y des Auftragnehmerkastens, oder `null` bei Einzelvergabe. */
    an: number | null;
    gewerke: number;
    /** Unterkante der Senkrechten, an der die Gewerke haengen. */
    spineEnde: number;
}

const zeilenPlan = (zweigZahl: number, mitAuftragnehmer: boolean): Zeilen => {
    const projekt = 30;
    const bauherr = 92;
    const ersterZweig = 150;
    const zweigAbstand = 44;
    const zweige = Array.from({ length: zweigZahl }, (_, i) => ersterZweig + i * zweigAbstand);
    const nachZweigen = zweige.length ? zweige[zweige.length - 1] + 30 : bauherr + BOX_H / 2 + 24;
    const an = mitAuftragnehmer ? nachZweigen + 30 : null;
    const spineEnde = an !== null ? an + BOX_H / 2 : nachZweigen;
    return { projekt, bauherr, zweige, an, gewerke: spineEnde + 26 + GEWERK_H / 2, spineEnde };
};

/** Waagerechte Aufteilung der Gewerkekaesten. Rechnet die Breite aus dem
 *  Platz — sonst zeichnet das Diagramm ab sieben Gewerken uebereinander
 *  (Fehler der Vorlage). */
const gewerkSpalten = (anzahl: number): { mitte: (i: number) => number; breite: number } => {
    const rand = 20;
    const platz = SVG_W - 2 * rand;
    const spalte = platz / Math.max(anzahl, 1);
    return {
        mitte: (i: number) => rand + spalte * (i + 0.5),
        breite: Math.max(52, Math.min(GEWERK_W, spalte - 8)),
    };
};

interface Vorankuendigungszeile {
    bezeichnung: string;
    wert: string;
    fehlt?: string;
}

/**
 * Die Pflichtangaben des §2 BaustellV, so wie sie in OS-IA gespeichert sind.
 *
 * Die Vorlage las eine flache `Vorankuendigung`; in OS-IA sind aus den
 * Einzelfeldern Listen geworden (Punkt 2, 4, 5a, 5b). Die Altfelder
 * (`coordinatorPlanning*`) bleiben als Rueckfall stehen — ein Projekt aus der
 * Zeit davor traegt seine Angaben nur dort.
 */
const vorankuendigungsZeilen = (v: VorankuendigungData): Vorankuendigungszeile[] => {
    const personen = (v.clientPersons || []).map(p => verbinde([p.name, p.role], ', ')).filter(Boolean);
    const dritte = (v.responsibleThirdParties || []).map(p => p.name).filter(Boolean);
    const planung = (v.planningCoordinators || []).map(k => verbinde([k.name, k.company], ', ')).filter(Boolean);
    const ausfuehrung = (v.executionCoordinators || []).map(k => verbinde([k.name, k.company], ', ')).filter(Boolean);
    const planungAlt = verbinde([v.coordinatorPlanningPerson, v.coordinatorPlanningCompany], ', ');
    const ausfuehrungAlt = verbinde([v.coordinatorExecutionPerson, v.coordinatorExecutionCompany], ', ');
    const gefaehrlich = v.hasDangerousWork
        ? `Ja — ${(v.selectedDangerousWorks || []).length} Arbeiten nach Anhang II`
        : 'Nein';

    return [
        { bezeichnung: 'Datum der Ankündigung', wert: tagText(v.locationDate) || v.locationDate || '', fehlt: 'nicht datiert' },
        { bezeichnung: 'Zuständige Behörde', wert: (v.recipient || '').split('\n')[0] || '', fehlt: 'keine Behörde eingetragen' },
        { bezeichnung: 'Ort der Baustelle', wert: (v.siteAddress || '').split('\n')[0] || '', fehlt: 'nicht angegeben' },
        { bezeichnung: 'Art des Bauvorhabens', wert: v.projectDescription || '', fehlt: 'nicht angegeben' },
        { bezeichnung: '2 — Bauherr, Personen', wert: personen.join(' | '), fehlt: 'keine Person benannt' },
        { bezeichnung: '4 — Verantwortliche Dritte', wert: dritte.join(' | ') || v.responsibleThirdParty || '', fehlt: 'keine benannt' },
        { bezeichnung: '5a — Koordinator Planung', wert: planung.join(' | ') || planungAlt, fehlt: 'nicht benannt' },
        { bezeichnung: '5b — Koordinator Ausführung', wert: ausfuehrung.join(' | ') || ausfuehrungAlt, fehlt: 'nicht benannt' },
        { bezeichnung: 'Baubeginn', wert: tagText(v.startDate) || v.startDate || '', fehlt: 'nicht angegeben' },
        { bezeichnung: 'Voraussichtliches Ende', wert: tagText(v.endDate) || v.endDate || '', fehlt: 'nicht angegeben' },
        { bezeichnung: '7 — Max. Beschäftigte', wert: v.maxWorkers || '', fehlt: 'nicht angegeben' },
        { bezeichnung: '8 — Anzahl Arbeitgeber', wert: v.numEmployers || '', fehlt: 'nicht angegeben' },
        { bezeichnung: '9 — Anzahl Unternehmer ohne Beschäftigte', wert: v.numContractors || '', fehlt: 'nicht angegeben' },
        { bezeichnung: '10 — Bereits ausgewählte Arbeitgeber', wert: (v.employersList || []).filter(Boolean).join(', '), fehlt: 'keine benannt' },
        { bezeichnung: 'Besonders gefährliche Arbeiten', wert: gefaehrlich },
    ];
};

// ═══════════════════════════════════════════════════════════════════════════
// SVG-GRUNDFORMEN — portiert aus `OrganigrammPage.tsx:28-72`
// ═══════════════════════════════════════════════════════════════════════════

const Linie: React.FC<{ x1: number; y1: number; x2: number; y2: number; gestrichelt?: boolean }> = ({
    x1, y1, x2, y2, gestrichelt,
}) => (
    <line
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke="#666666" strokeWidth={0.8}
        strokeDasharray={gestrichelt ? '4 3' : undefined}
    />
);

const OrgKasten: React.FC<{
    x: number; y: number;
    label: string;
    sub?: string;
    gestrichelt?: boolean;
    stark?: boolean;
    fuellung?: string;
    breite?: number;
    hoehe?: number;
    /** Der Kasten steht, die Rolle ist aber unbesetzt. */
    unbesetzt?: boolean;
}> = ({
    x, y, label, sub, gestrichelt, stark, fuellung = '#ffffff',
    breite = BOX_W, hoehe = BOX_H, unbesetzt,
}) => {
    const rx = x - breite / 2;
    const ry = y - hoehe / 2;
    return (
        <g>
            <rect
                x={rx} y={ry} width={breite} height={hoehe}
                fill={fuellung}
                stroke={stark ? '#000000' : '#555555'}
                strokeWidth={stark ? 2 : 1}
                strokeDasharray={gestrichelt ? '4 3' : undefined}
                rx={2}
            />
            <text
                x={x} y={sub || unbesetzt ? ry + hoehe / 2 - 2 : ry + hoehe / 2 + 4}
                textAnchor="middle" fontSize={8} fontWeight="bold" fill={HANDBUCH_FARBEN.text}
            >
                {kuerze(label, 24)}
            </text>
            {(sub || unbesetzt) && (
                <text
                    x={x} y={ry + hoehe / 2 + 8}
                    textAnchor="middle" fontSize={7}
                    fill={unbesetzt ? HANDBUCH_FARBEN.statusFehlt : '#555555'}
                    fontStyle={unbesetzt ? 'italic' : undefined}
                >
                    {/* „nicht hinterlegt" statt eines leeren Kastens: die Rolle
                        gehoert zum Vergabemodell, nur ihr Traeger fehlt. */}
                    {unbesetzt ? 'nicht hinterlegt' : kuerze(sub || '', 22)}
                </text>
            )}
        </g>
    );
};

const GewerkKastenSvg: React.FC<{ g: GewerkKasten; x: number; y: number; breite: number }> = ({
    g, x, y, breite,
}) => {
    const rx = x - breite / 2;
    const ry = y - GEWERK_H / 2;
    const zeitraum = g.von || g.bis
        ? `${monatText(g.von) || '?'} – ${monatText(g.bis) || '?'}`
        : '';
    const maxZeichen = Math.max(8, Math.round(breite / 5.4));

    return (
        <g>
            <rect
                x={rx} y={ry} width={breite} height={GEWERK_H}
                fill="#fafafa" stroke="#555555" strokeWidth={0.8} rx={2}
            />
            <text x={x} y={ry + 11} textAnchor="middle" fontSize={7.5} fontWeight="bold" fill={HANDBUCH_FARBEN.text}>
                {kuerze(g.label, maxZeichen)}
            </text>
            <text x={x} y={ry + 21} textAnchor="middle" fontSize={6.5} fill="#555555">
                {g.firma ? kuerze(g.firma, maxZeichen) : '—'}
            </text>
            {zeitraum ? (
                <text x={x} y={ry + 30} textAnchor="middle" fontSize={6} fill="#888888">
                    {zeitraum}
                </text>
            ) : (
                /* Ein Gewerk ohne Zeitraum faellt aus jeder Ueberschneidungs-
                   rechnung heraus. Das muss man sehen, nicht erraten. */
                <text x={x} y={ry + 30} textAnchor="middle" fontSize={6} fill={HANDBUCH_FARBEN.statusOffen}>
                    ohne Zeitraum
                </text>
            )}
            <line x1={rx + 3} y1={ry + 34} x2={rx + breite - 3} y2={ry + 34} stroke="#cccccc" strokeWidth={0.5} />
            {g.nurPlan ? (
                <text x={rx + 4} y={ry + 45} fontSize={6} fill="#888888" fontStyle="italic">
                    aus dem Plan
                </text>
            ) : g.dokGesamt > 0 ? (
                <g>
                    <text x={rx + 4} y={ry + 45} fontSize={6} fill="#555555">Nachw.</text>
                    <text x={rx + 30} y={ry + 45} fontSize={6} fill={HANDBUCH_FARBEN.statusOk}>✓{g.dokOk}</text>
                    {g.dokPruef > 0 && (
                        <text x={rx + 45} y={ry + 45} fontSize={6} fill={HANDBUCH_FARBEN.statusOffen}>○{g.dokPruef}</text>
                    )}
                    {g.dokFehlt > 0 && (
                        <text
                            x={rx + (g.dokPruef > 0 ? 60 : 45)} y={ry + 45}
                            fontSize={6} fontWeight="bold" fill={HANDBUCH_FARBEN.statusFehlt}
                        >
                            ✗{g.dokFehlt}
                        </text>
                    )}
                </g>
            ) : (
                <text x={rx + 4} y={ry + 45} fontSize={6} fill="#999999">keine Nachweise</text>
            )}
            {g.personen !== null && (
                <text x={rx + breite - 4} y={ry + 45} textAnchor="end" fontSize={6} fill="#888888">
                    {g.personen} Pers.
                </text>
            )}
        </g>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// DAS DIAGRAMM
// ═══════════════════════════════════════════════════════════════════════════

const OrgDiagramm: React.FC<{
    konfig: OrgModellKonfig;
    projektName: string;
    bauherr: string;
    kaesten: Kasten[];
    sigeko: Kasten;
    gewerke: GewerkKasten[];
}> = ({ konfig, projektName, bauherr, kaesten, sigeko, gewerke }) => {
    const anRolle = konfig.beteiligteRollen.find(r => r === 'gu' || r === 'gue' || r === 'tu') || null;
    const zweige = kaesten.filter(k => k.rolle !== anRolle);
    const anKasten = anRolle ? kaesten.find(k => k.rolle === anRolle) : undefined;
    const z = zeilenPlan(zweige.length, !!anKasten);

    const sichtbar = gewerke.slice(0, MAX_GEWERKE_SICHTBAR);
    const weitere = gewerke.length - sichtbar.length;
    const spalten = gewerkSpalten(sichtbar.length);
    /* Die Gewerke haengen am Auftragnehmer, wo es einen gibt — sonst direkt am
       Bauherrn. Das ist der fachliche Kern des Vergabemodells und steht
       deshalb in `orgModelle.ts` (`gewerkeUnter`) und nicht hier. */
    const gewerkeVaterY = z.an !== null ? z.an : z.bauherr;
    const tBalkenY = z.gewerke - GEWERK_H / 2 - 14;

    return (
        <svg
            viewBox={`0 0 ${SVG_W} ${SVG_H}`}
            width="100%" height="100%"
            preserveAspectRatio="xMidYMid meet"
            style={{ fontFamily: HANDBUCH_SCHRIFT }}
        >
            {/* Projekt → Bauherr */}
            <OrgKasten x={CX} y={z.projekt} label={projektName || 'Projekt'} stark fuellung="#f0f0f0" />
            <Linie x1={CX} y1={z.projekt + BOX_H / 2} x2={CX} y2={z.bauherr - BOX_H / 2} />
            <OrgKasten
                x={CX} y={z.bauherr}
                label="Bauherr"
                sub={bauherr}
                unbesetzt={!bauherr.trim()}
                stark
            />

            {/* SiGeKo als Stabsstelle: gestrichelt, seitlich am Bauherrn, NICHT
                in der Linie. Er berät und ist nicht weisungsbefugt. */}
            <Linie
                x1={CX + BOX_W / 2} y1={z.bauherr}
                x2={CX + ZWEIG_DX - BOX_W / 2} y2={z.bauherr}
                gestrichelt
            />
            <OrgKasten
                x={CX + ZWEIG_DX} y={z.bauherr}
                label={sigeko.label}
                sub={sigeko.sub}
                unbesetzt={sigeko.fehlt}
                gestrichelt
            />

            {/* Senkrechte unter dem Bauherrn, an der alles Weitere haengt */}
            <Linie x1={CX} y1={z.bauherr + BOX_H / 2} x2={CX} y2={z.an !== null ? z.an - BOX_H / 2 : tBalkenY} />

            {/* Seitenzweige: Planer, Bauleitung … abwechselnd links und rechts */}
            {zweige.map((k, i) => {
                const links = i % 2 === 0;
                const x = links ? CX - ZWEIG_DX : CX + ZWEIG_DX;
                const y = z.zweige[i];
                return (
                    <g key={k.rolle}>
                        <Linie x1={CX} y1={y} x2={links ? x + BOX_W / 2 : x - BOX_W / 2} y2={y} />
                        <OrgKasten x={x} y={y} label={k.label} sub={k.sub} unbesetzt={k.fehlt} />
                    </g>
                );
            })}

            {/* Auftragnehmer (GU / GÜ / TU) in der Linie */}
            {anKasten && z.an !== null && (
                <OrgKasten
                    x={CX} y={z.an}
                    label={anKasten.label}
                    sub={anKasten.sub}
                    unbesetzt={anKasten.fehlt}
                    stark
                    breite={BOX_W + 30}
                />
            )}
            {anKasten && z.an !== null && (
                <Linie x1={CX} y1={z.an + BOX_H / 2} x2={CX} y2={tBalkenY} />
            )}

            {/* Gewerke */}
            {sichtbar.length === 0 && (
                <>
                    <Linie x1={CX} y1={gewerkeVaterY + BOX_H / 2} x2={CX} y2={z.gewerke - GEWERK_H / 2} />
                    <OrgKasten
                        x={CX} y={z.gewerke}
                        label="Keine Gewerke"
                        sub="im Plan keine Arbeitsschritte"
                        fuellung="#fafafa"
                        breite={GEWERK_W + 60}
                        hoehe={GEWERK_H}
                    />
                </>
            )}
            {sichtbar.length > 0 && (
                <>
                    {sichtbar.length > 1 && (
                        <Linie
                            x1={spalten.mitte(0)} y1={tBalkenY}
                            x2={spalten.mitte(sichtbar.length - 1)} y2={tBalkenY}
                        />
                    )}
                    {sichtbar.map((g, i) => (
                        <g key={g.schluessel}>
                            <Linie
                                x1={spalten.mitte(i)} y1={tBalkenY}
                                x2={spalten.mitte(i)} y2={z.gewerke - GEWERK_H / 2}
                            />
                            <GewerkKastenSvg g={g} x={spalten.mitte(i)} y={z.gewerke} breite={spalten.breite} />
                        </g>
                    ))}
                </>
            )}
            {weitere > 0 && (
                /* Nicht stillschweigend abschneiden: die Zahl steht da, und die
                   vollstaendige Aufstellung ist im Kapitel „Gewerkeseiten". */
                <text
                    x={SVG_W - 20} y={z.gewerke + GEWERK_H / 2 + 12}
                    textAnchor="end" fontSize={7} fill="#888888"
                >
                    + {weitere} weitere Gewerke — vollständig im Kapitel „Gewerkeseiten"
                </text>
            )}

            {/* Legende */}
            <g transform={`translate(10, ${SVG_H - 26})`}>
                <rect x={0} y={0} width={86} height={14} fill="#ffffff" stroke="#555555" strokeWidth={1} strokeDasharray="4 3" rx={1} />
                <text x={43} y={9} textAnchor="middle" fontSize={7} fill="#555555">SiGeKo (beratend)</text>
                <rect x={94} y={0} width={86} height={14} fill="#ffffff" stroke="#000000" strokeWidth={2} rx={1} />
                <text x={137} y={9} textAnchor="middle" fontSize={7}>Vertragspartner</text>
                <rect x={188} y={0} width={86} height={14} fill="#fafafa" stroke="#555555" strokeWidth={0.8} rx={1} />
                <text x={231} y={9} textAnchor="middle" fontSize={7} fill="#555555">Gewerk</text>
                <text x={286} y={9} fontSize={7} fill="#888888">
                    {`Vergabemodell: ${konfig.label}`}
                </text>
                <text x={510} y={9} fontSize={6.5} fill="#555555">Nachweise:</text>
                <text x={556} y={9} fontSize={6.5} fill={HANDBUCH_FARBEN.statusOk}>✓ vorhanden</text>
                <text x={614} y={9} fontSize={6.5} fill={HANDBUCH_FARBEN.statusFehlt}>✗ fehlt</text>
            </g>
        </svg>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// DAS KAPITEL
// ═══════════════════════════════════════════════════════════════════════════

const SprungKnopf: React.FC<{
    text: string;
    ziel: SprungZiel;
    onSprung?: (ziel: SprungZiel) => void;
}> = ({ text, ziel, onSprung }) => (onSprung ? (
    <button
        type="button"
        onClick={() => onSprung(ziel)}
        className="print:hidden underline"
        style={{ fontSize: '8px', color: HANDBUCH_FARBEN.textLeise }}
    >
        {text}
    </button>
) : null);

export const Organigramm: React.FC<Props> = ({
    metadata, handbuch, vorankuendigung, gewerke, planGewerke = [], nachweise = [],
    kopf, fuss, onSprung,
}) => {
    const konfig = orgModellKonfig(handbuch?.org_modell);
    const sigeko = sigekoKasten(metadata);
    const kaesten = konfig ? besetzung(konfig, metadata) : [];
    const kaestenGewerke = gewerkKaesten(gewerke, planGewerke, nachweise);
    const verteiler = metadata.distributionList || [];
    // Die Tabelle rechts unten hat Platz fuer eine Handvoll Zeilen; der Rest
    // wird gezaehlt statt weggelassen.
    const verteilerSichtbar = verteiler.slice(0, 9);

    return (
        <A3Seite kopf={kopf} fuss={fuss}>
            <div className="flex-1 flex gap-[8px] min-h-0 overflow-hidden">

                {/* ═══ LINKS: das Diagramm ═══ */}
                <div className="flex flex-col min-h-0" style={{ width: '62%' }}>
                    <Abschnitt
                        titel="Organigramm — Projektstruktur"
                        zusatz={konfig ? konfig.label : undefined}
                        className="flex-1"
                    >
                        {konfig ? (
                            <div className="w-full h-full">
                                <OrgDiagramm
                                    konfig={konfig}
                                    projektName={metadata.projectName || ''}
                                    bauherr={metadata.clientName || ''}
                                    kaesten={kaesten}
                                    sigeko={sigeko}
                                    gewerke={kaestenGewerke}
                                />
                            </div>
                        ) : (
                            /* Ohne Vergabemodell wird NICHTS gezeichnet. Ein
                               geratenes Modell zeichnet ein falsches
                               Organigramm — und ein falsches Organigramm sieht
                               richtig aus. */
                            <div className="flex flex-col items-start gap-1">
                                <AbschnittLeer text="Vergabemodell noch nicht bestimmt — ohne es bleibt das Organigramm leer. Geraten wird hier nichts." />
                                <SprungKnopf
                                    text="→ Vergabemodell im Handbuch festlegen"
                                    ziel="handbuch"
                                    onSprung={onSprung}
                                />
                            </div>
                        )}
                    </Abschnitt>
                </div>

                {/* ═══ RECHTS: Vorankündigung und Beteiligte ═══ */}
                <div className="flex flex-col gap-[6px] min-h-0" style={{ width: '38%' }}>

                    <Abschnitt titel="Vorankündigung gemäß § 2 BaustellV" className="flex-1">
                        {vorankuendigung ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '7.5px' }}>
                                <tbody>
                                    {vorankuendigungsZeilen(vorankuendigung).map((z, i) => (
                                        <tr key={z.bezeichnung} style={i === 0 ? undefined : { borderTop: '1px solid #eeeeee' }}>
                                            <td style={{
                                                padding: '1.5px 4px 1.5px 0',
                                                width: '52%',
                                                verticalAlign: 'top',
                                                color: HANDBUCH_FARBEN.textLeise,
                                            }}>
                                                {z.bezeichnung}
                                            </td>
                                            <td style={{ padding: '1.5px 0', verticalAlign: 'top' }}>
                                                {z.wert
                                                    ? <span style={{ fontWeight: 600 }}>{kuerze(z.wert, 60)}</span>
                                                    : (
                                                        <span style={{
                                                            color: HANDBUCH_FARBEN.statusFehlt,
                                                            fontStyle: 'italic',
                                                        }}>
                                                            {z.fehlt || '—'}
                                                        </span>
                                                    )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="flex flex-col items-start gap-1">
                                <AbschnittLeer text="Für dieses Projekt ist noch keine Vorankündigung erstellt. § 2 BaustellV verlangt sie, sobald die Baustelle die Schwellen überschreitet." />
                                <SprungKnopf
                                    text="→ Vorankündigung öffnen"
                                    ziel="vorankuendigung"
                                    onSprung={onSprung}
                                />
                            </div>
                        )}
                    </Abschnitt>

                    <Abschnitt
                        titel="Beteiligte"
                        zusatz={verteiler.length > verteilerSichtbar.length
                            ? `${verteilerSichtbar.length} von ${verteiler.length}`
                            : undefined}
                        style={{ flex: '0 0 auto', maxHeight: '38%' }}
                    >
                        {verteiler.length === 0 ? (
                            <div className="flex flex-col items-start gap-1">
                                <AbschnittLeer text="Im Projektverteiler steht noch niemand. Die Rollen im Organigramm bleiben dadurch unbesetzt." />
                                <SprungKnopf text="→ Verteiler öffnen" ziel="verteiler" onSprung={onSprung} />
                            </div>
                        ) : (
                            <AbschnittTabelle
                                kopf={['Rolle / Gewerk', 'Firma / Person', 'Telefon']}
                                breiten={['32%', '44%', '24%']}
                            >
                                {verteilerSichtbar.map((k, i) => (
                                    <AbschnittZeile key={k.id} erste={i === 0}>
                                        <AbschnittZelle style={{ color: HANDBUCH_FARBEN.textLeise, fontSize: '8px' }}>
                                            {kuerze(k.role || k.tradeType || '—', 26)}
                                        </AbschnittZelle>
                                        <AbschnittZelle style={{ fontSize: '8px' }}>
                                            {kuerze(k.company || k.name || '—', 30)}
                                        </AbschnittZelle>
                                        <AbschnittZelle style={{ fontSize: '8px' }}>
                                            {k.phone || '—'}
                                        </AbschnittZelle>
                                    </AbschnittZeile>
                                ))}
                            </AbschnittTabelle>
                        )}
                    </Abschnitt>
                </div>
            </div>
        </A3Seite>
    );
};
